import { emsLightGrey, emsWarningRed } from "@electricMotorShop/EmsVariables";
import ProductAddedToCartMessage from "@electricMotorShop/Overrides/Components/ProductAddedToCartMessage";
import { xcCanAddToCart } from "@electricMotorShop/Store/Data/Products/XcProductsSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasCartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XC customization - Using xcCanAddToCart from XcProductsSelectors instead of original ProductsSelectors
// Display Discontinued Message

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    product: ProductModel;
    showUnitOfMeasure?: boolean;
    onAddToCartClick?: () => void;
    onClick?: React.EventHandler<React.MouseEvent>;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasCartLineContext &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    const replacementProduct =
        props.productContext.product && props.productContext.product.properties["replacementProductUrl"]
            ? props.productContext.product.properties["replacementProductUrl"]
            : null;

    const additionalResults = props.productContext.productInfo.inventory?.additionalResults;
    const inventoryBackOrder = additionalResults && Object.keys(additionalResults).some(key=>key === "backOrder");
    
    const backOrderItem =  props.productContext.product && Object.keys(props.productContext.product.properties).some(key=>key === "backOrder") 
    ? props.productContext.product.properties["backOrder"].toLowerCase() === "true" 
    : false;
    const priceNotMaintained = props.productContext.product && Object.keys(props.productContext.product.properties).some(key=>key === "priceNotMaintained") 
    ? props.productContext.product.properties["priceNotMaintained"].toLowerCase() === "true" 
    : false;

    const selectedImage = getSelectedImage(state, props.productContext.product);


    return {
        canAddToCart: xcCanAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        isDiscontinued: props.productContext.product ? props.productContext.product.isDiscontinued : false,
        replacementProductUrl: replacementProduct,
        product: props.productContext.product,
        image: selectedImage,
        backOrderItem,
        inventoryBackOrder,
        priceNotMaintained,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

export interface ProductAddToCartButtonStyles {
    discontinuedTypography?: TypographyPresentationProps;
    inquiryWrapper?: InjectableCss;
    inquiryTypography?: TypographyPresentationProps;
    replacementProductLink?: LinkPresentationProps;
    unitOfMeasure?: UnitOfMeasureStyles;
    /**
     * @deprecated Use the `unitOfMeasure.unitOfMeasureText` property instead.
     */
    unitOfMeasureText?: TypographyPresentationProps;
    priceWrapper?: InjectableCss;
    inquiryButton?: ButtonPresentationProps;
}

interface UnitOfMeasureStyles {
    unitOfMeasureText?: TypographyPresentationProps;
}

export const productAddToCartButtonStyles: ProductAddToCartButtonStyles = {
    discontinuedTypography: {
        css: css`
            font-weight: bold;
            padding-right: 10px;
            width: 50%;
        `,
    },
    inquiryWrapper: {
        css: css`
            background-color: ${emsLightGrey};
            min-height: 40px;
            align-items: center;
            width: 100%;
            display: flex;
            justify-content: center;
            border-radius: 4px;
        `,
    },
    inquiryTypography: {
        css: css`
            font-weight: bold;
            margin-top: 0;
        `,
    },
    replacementProductLink: {
        color: emsWarningRed,
        css: css`
            font-weight: bold;
            width: 50%;
            span {
                text-decoration: underline;
            }
        `,
    },
    unitOfMeasure: {
            unitOfMeasureText: {
                color: "primary",
                css: css`
                    white-space: nowrap;
                    font-weight: bold;
                    margin-left: 4px;
                `,
            },
        },
        priceWrapper: {
        css: css`
            ${wrapInContainerStyles}
            display: flex;
            align-items: baseline;
        `,
    },
    inquiryButton: {
        css: css`
            background-color: #666666;
            border-color: #666666;
            color: #ffffff;
            width: 100%;

            &:hover,
            &:focus,
            &:active {
                background-color: #474747;
                border-color: #474747;
            }
        `,
    },
};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired, productTitle, properties, unitOfMeasures },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    isDiscontinued,
    cartLine,
    replacementProductUrl,
    image,
    product,
    onClick,
    backOrderItem,
    inventoryBackOrder,
    priceNotMaintained,
    showUnitOfMeasure = true,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const history = useHistory();

    const handleButtonClick = () => {
        const cartUrl = "/cart";
        history.push(cartUrl);
    };
    
    if (isDiscontinued) {
        return (
            <>
                <Typography {...styles.discontinuedTypography}>
                    {siteMessage("Lists_Item_Not_Displayed_Due_To_Discontinued")}
                </Typography>

                {replacementProductUrl && (
                    <Link href={replacementProductUrl} onClick={onClick} {...styles.replacementProductLink}>
                        {translate("View Replacement")}
                    </Link>
                )}
            </>
        );
    }

    if (!hasEnoughInventory && !failedToLoadInventory) {
        return null;
    }

    const backOrder = (backOrderItem || (inventoryBackOrder && canAddToCart));
    const displayOnlyDetail = properties?.displayOnlyDetail;
    const displayonCallForPricing = properties?.displayonCallForPricing;

    const unitOfMeasureDescription =
        unitOfMeasures?.find(o => o.unitOfMeasure === unitOfMeasure)
                  ?.description
            
    const unitOfMeasureDisplay =
       unitOfMeasures?.find(o => o.unitOfMeasure === unitOfMeasure)
                  ?.unitOfMeasureDisplay
    
    // Determine which text message to display (prefer Call For Pricing override if present)
    const uomDetailText = displayonCallForPricing || displayOnlyDetail;

    if ((!canAddToCart && !backOrder) || priceNotMaintained) {
        return (
            <StyledWrapper
                css={css`
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                `}
            >
                {(showUnitOfMeasure && unitOfMeasure) || uomDetailText ? (
                    <StyledWrapper
                        css={css`
                            display: flex;
                            align-items: baseline;
                            margin-bottom: 12px;
                        `}
                    >
                        {/* Hide default UOM if displayonCallForPricing is actively overriding it */}
                        {!displayonCallForPricing && (
                            <UnitOfMeasure
                                unitOfMeasure={unitOfMeasureDescription || unitOfMeasureDisplay}
                                showUnitOfMeasure={unitOfMeasure !== "EA"}
                                styles={styles.unitOfMeasure}
                            />
                        )}

                        {uomDetailText && (
                            <Typography {...styles.unitOfMeasure?.unitOfMeasureText}>
                                {uomDetailText}
                            </Typography>
                        )}
                    </StyledWrapper>
                ) : null}

                <Button
                    {...styles.inquiryButton}
                    onClick={() => {
                        history.push(`/Contact-for-Price-And-Availability?productId=${productId}`);
                    }}
                >
                    {siteMessage("AddToCart_CallOrEmailForInquiry")}
                </Button>
            </StyledWrapper>
        );
    }

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                messageType: "success",
                body: (
                    <ProductAddedToCartMessage
                        isQtyAdjusted={true}
                        multipleProducts={false}
                        productTitle={productTitle}
                        selectedImage={image}
                        product={product}
                        onButtonClick={handleButtonClick}
                    />
                ),
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
                cssOverrides: {
                    toast: css`
                        border: none;
                    `,
                    toastBody: css`
                        margin: 10px 44px 10px 10px;
                    `,
                },
            });
        }
    };

    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
            {...otherProps}
        >
            {backOrder ? translate("Backorder") :  labelOverride ?? translate("Add to Cart")}
        </Button>
    );
};
interface UnitOfMeasureStyles {
    unitOfMeasureText?: TypographyPresentationProps;
}
interface UnitOfMeasureProps {
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    styles?: UnitOfMeasureStyles;
}

const UnitOfMeasure = ({ unitOfMeasure, showUnitOfMeasure = true, styles }: UnitOfMeasureProps) => {
    
    return unitOfMeasure && showUnitOfMeasure ? (
        <Typography {...styles?.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
            {unitOfMeasure}
        </Typography>
    ) : null;
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
